import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { upperFirst } from 'lodash'
import { LoaderCircle, RefreshCw, RefreshCwOff, UploadIcon } from 'lucide-react'
import { Button, cn, DefaultTooltip, InputGroup, InputGroupText } from 'twyne-new.ui'

import { useAppAlert } from '@/components/AppAlert'
import { useBuilderStore } from '@/components/builder/utils/store'
import { useGlobalTailwindConfig, useMetadata } from '@/components/builder/utils/tailwindConfig/hooks'
import { DefaultChangeHistoryHeader } from '@/components/change-history'
import AgentBadge from '@/modules/cms/views/post/components/builder/agent/AgentBadge'
import { useBuilder, useAutoSave } from '@/modules/cms/views/post/components/builder/context'
import routeStore from 'modules/cms/stores/route'
import appStore from 'stores/app';

import { usePostStore } from '../../../store'
import { useFormMetaParams } from '../hooks/useMetadata'

import { AssetsButton, BlocksButton, LayersButton } from './Toolbar'

export default function Header() {
  return <div className='relative z-10 h-16 px-4 flex items-center justify-between border-b twy-border-base twy-bg-base-accent'>
    <div className='flex items-center gap-1'>
      <AssetsButton size='sm' />
      <LayersButton size='sm' />
      <BlocksButton size='sm' />
    </div>
    <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 h-7 px-2.5 rounded-md border twy-border-base twy-bg-base leading-4'>
      <Status />
    </div>
    <div className='flex items-center gap-2'>
      <AgentBadge />
      <ChangeHistoryStatus />
      <Actions />
    </div>
  </div>
}

const STATUS = {
  draft: { label: 'Draft not saved', dot: 'twy-bg-danger' },
  unsaved: { label: 'Unsaved changes', dot: 'twy-bg-neutral' },
  notPublished: { label: 'Saved, not published', dot: 'twy-bg-warning' },
  unpublishedChanges: { label: 'Unpublished changes', dot: 'twy-bg-warning' },
  published: { label: 'Published', dot: 'twy-bg-success' },
}

function AutoSaveToggle() {
  const { toggleAutoSave, disableAutoSave } = useBuilderStore()

  return <DefaultTooltip content={disableAutoSave ? 'Enable auto-save' : 'Disable auto-save'} triggerProps={{ asChild: true }}>
    <Button className='h-8' color={disableAutoSave ? 'neutral' : 'accent'} variant='outline' onClick={toggleAutoSave}>
      {disableAutoSave ? <RefreshCwOff size={16} /> : <RefreshCw size={16} />}
    </Button>
  </DefaultTooltip>
}

function Status() {
  const { saved, autoSaved } = useAutoSave()
  const { postBuilder: { item } } = useBuilderStore()
  const { version, publishVersion } = item?.meta || {}
  const hasBuilder = Boolean(item?.id)

  const status = useMemo(() => {
    if (!hasBuilder || !version) {
      return STATUS.draft
    }

    if (!saved) {
      return STATUS.unsaved
    }

    if (!publishVersion) {
      return STATUS.notPublished
    }

    if (!autoSaved && version === publishVersion) {
      return STATUS.published
    }

    return STATUS.unpublishedChanges
  }, [hasBuilder, saved, autoSaved, version, publishVersion])

  return <div className='flex items-center gap-2 twy-text-base-accent'>
    <span className={cn('size-2 rounded-full', status.dot)} />
    <span>{status.label}</span>
  </div>
}

export function useSitePath() {
  const [sitePath, setSitePath] = useState()
  const { collection: { data } = {} } = usePostStore() || {}
  const { postBuilder: { version: { item: version } = {} } } = useBuilderStore()
  const post_id = data?.id
  const handleAddFormParam = useFormMetaParams()

  useEffect(() => {
    if (!post_id) {
      setSitePath(null)
      return
    }

    !async function loadRoute() {
      const routes = await routeStore.all(appStore.site.id).then(res => res.data && res.data.rows);
      const route = routes
        .find(d => d.entity_id === post_id)

      let sitePath = null
      if (route) {
        const url = new URL(`https://${appStore.site.domain}${route.path}`)
        url.searchParams.set('nocache', 'true')
        url.searchParams.set('force_version', 'true')
        version?.slug && url.searchParams.set('__version', version.slug)
        handleAddFormParam(url.searchParams)
        if (process.env.NODE_ENV === 'development') {
          url.port = 3002
          url.protocol = 'http'
        }
        sitePath = url.href
      }

      setSitePath(sitePath)
    }()
  }, [post_id, handleAddFormParam, version])

  return sitePath
}

function ChangeHistoryStatus() {
  const { postBuilder, loading } = useBuilderStore()
  return <DefaultChangeHistoryHeader entityCollection={postBuilder} loading={loading} />
}

function Actions() {
  const { postBuilder: { history: { preview } }, loading: storeLoading } = useBuilderStore()
  const { content_type: contentType } = useParams()
  const { handleSave: onSave, handlePublish: onPublish } = useBuilder()
  const { markSaved } = useAutoSave()
  const { handleSuccess, handleError } = useAppAlert()
  const tailwindConfig = useGlobalTailwindConfig()
  const { value: meta } = useMetadata()

  const [loading, setLoading] = useState('')
  const sitePath = useSitePath()
  const [popup, setPopup] = useState(false)

  const entity = useMemo(() => upperFirst(contentType), [contentType])
  const handleOpen = useCallback(() => window.open(sitePath, 'preview'), [sitePath])

  const handleSave = useCallback(async () => {
    setLoading('saving')
    try {
      await onSave()
      markSaved()
      handleSuccess('Success', `${entity} saved.`)
    } catch (error) {
      handleError('Error', `${entity} was not saved.`)
    }
    setLoading('')
  }, [onSave, markSaved, entity, handleSuccess, handleError])

  const handlePublish = useCallback(async () => {
    setLoading('publishing')
    try {
      await onPublish({ tailwindConfig, meta })
      markSaved()
      handleSuccess('Success', `${entity} published.`)

      setPopup(popoup => {
        if (popoup && !popoup.closed) {
          popoup.location.replace(sitePath)
          popoup.focus()
          return popoup
        } else {
          return handleOpen()
        }
      })
    } catch (error) {
      handleError('Error', `${entity} was not published.`)
    }
    setLoading('')
  }, [onPublish, markSaved, entity, handleSuccess, handleError, tailwindConfig, meta, sitePath, handleOpen])

  if (preview) {
    return null
  }

  const disabled = !!loading || !!storeLoading

  return <>
    <InputGroup button>
      <InputGroupText addonType='prepend'>
        <AutoSaveToggle />
      </InputGroupText>
      <InputGroupText addonType='append'>
        <Button className='items-center h-8' color='neutral' variant='outline' onClick={handleSave} disabled={disabled}>
          {loading === 'saving' && <LoaderCircle className='animate-spin' size={16} />}
          Save draft
        </Button>
      </InputGroupText>
    </InputGroup>
    <Button className='items-center h-8' color='accent' onClick={handlePublish} disabled={disabled}>
      {loading === 'publishing' ? <LoaderCircle className='animate-spin' size={16} /> : <UploadIcon size={16} />}
      Publish
    </Button>
  </>
}
